/**
 * ProfileTabs Component
 *
 * Tab navigation for profile page (Overview, Orders, Settings)
 */

'use client';

import { useTranslations } from 'next-intl';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { Breadcrumb } from '@/src/shared/ui/navigation';
import { Avatar } from '@/src/shared/ui/avatars/Avatar';

interface BreadcrumbItem {
  label: string;
  isActive: boolean;
}

export interface ProfileTabsProps {
  activeTab: 'overview' | 'orders' | 'settings';
  onTabChange: (tab: 'overview' | 'orders' | 'settings') => void;
  breadcrumbItems?: BreadcrumbItem[];
  isCompact?: boolean;
  name?: string;
  avatarUrl?: string | null;
  isOwnProfile?: boolean;
}

export function ProfileTabs({
  activeTab,
  onTabChange,
  breadcrumbItems = [],
  isCompact = false,
  name = '',
  avatarUrl = null,
  isOwnProfile = false,
}: ProfileTabsProps) {
  const t = useTranslations('profile');

  return (
    <div className={`pb-3 mb-4 flex flex-row items-end justify-between transition-all duration-300 ${
      isCompact ? 'h-auto' : 'h-[103px]'
    }`}>
        {/* Breadcrumb - Hidden in compact mode */}
        {!isCompact && breadcrumbItems.length > 0 && (
            <div className="transition-opacity duration-300">
            <Breadcrumb items={breadcrumbItems} textVariant="h3" />
            </div>
        )}
       {isCompact && isOwnProfile && (
        <button
          onClick={() => onTabChange('overview')}
          className="flex items-center gap-2.5 mb-4 animate-fadeIn hover:opacity-80 transition-opacity"
        >
          <div className="relative">
            <Avatar
              src={avatarUrl || undefined}
              alt={name}
              size="xs"
              fallback={name.charAt(0).toUpperCase()}
              className="transition-all duration-300 ease-in-out"
            />
          </div>
          <Typography
            variant="h3"
            weight="semibold"
            className="text-text-primary animate-fadeIn"
          >
            {t('profile')}
          </Typography>
        </button>
      )}


      <div className="flex flex-row items-center gap-6">
        
        <button
          onClick={() => onTabChange('orders')}
          className={`flex flex-row items-center gap-2 transition-colors ${
            activeTab === 'orders'
              ? 'text-accent-primary'
              : 'text-text-primary hover:text-accent-primary'
          }`}
        >
          <Icon name="orders" size="md" />
          <Typography variant="h3" weight="bold">
            {t('tabs.orders')}
          </Typography>
        </button>

        <button
          onClick={() => onTabChange('settings')}
          className={`flex flex-row items-center gap-2 transition-colors ${
            activeTab === 'settings'
              ? 'text-accent-primary'
              : 'text-text-primary hover:text-accent-primary'
          }`}
        >
          <Icon name="settings" size="md" />
          <Typography variant="h3" weight="bold">
            {t('tabs.settings')}
          </Typography>
        </button>
      </div>
    </div>
  );
}
